import React, { FC, useEffect, useState } from 'react'
import { Button, Grid, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { DateRange } from '@mui/x-date-pickers-pro'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { DateRangePresetPicker } from 'src/components/ui'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { buildOrderDateRangeQueryValues, parseOrderDateParam } from './orderDateRangeParams'

interface OrderDateRangeFilterProps {
  // false = toolbar mode: just the range field with an inline clear button, no preset row
  showPresets?: boolean
}

// Reuses the same date-range component as the "Download measurements" feature,
// but drives the orderDateFrom/orderDateTo URL search params
export const OrderDateRangeFilter: FC<OrderDateRangeFilterProps> = ({ showPresets = true }) => {
  const [queryParams, setQueryParams, removeQueryParams] = useQueryParams()
  const [range, setRange] = useState<DateRange<Date>>([
    parseOrderDateParam(queryParams[SearchParamsKeys.OrderDateFrom]),
    parseOrderDateParam(queryParams[SearchParamsKeys.OrderDateTo])
  ])
  const [dateFrom, dateTo] = range
  const paramFrom = queryParams[SearchParamsKeys.OrderDateFrom]
  const paramTo = queryParams[SearchParamsKeys.OrderDateTo]

  // Keep the field in sync when the params change from outside (e.g. "Clear all" chips action)
  useEffect(() => {
    if (!paramFrom && !paramTo) setRange((current) => (current[0] || current[1] ? [null, null] : current))
  }, [paramFrom, paramTo])

  const handleChange = async (newRange: DateRange<Date>) => {
    setRange(newRange)
    const queryValues = buildOrderDateRangeQueryValues(newRange[0], newRange[1])
    if (queryValues) {
      await setQueryParams(queryValues, [SearchParamsKeys.OrderDateFrom, SearchParamsKeys.OrderDateTo])
    }
  }

  const handleClear = async () => {
    setRange([null, null])
    await removeQueryParams([SearchParamsKeys.OrderDateFrom, SearchParamsKeys.OrderDateTo])
  }

  if (!showPresets) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateRangePicker
          value={range}
          onChange={handleChange}
          slots={{ field: SingleInputDateRangeField }}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 230 },
              InputProps: {
                endAdornment:
                  dateFrom || dateTo ? (
                    <IconButton aria-label="Clear order date filter" size="small" onClick={handleClear} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
              }
            }
          }}
          label="Order date"
        />
      </LocalizationProvider>
    )
  }

  return (
    <Grid container alignItems="flex-start" spacing={1} flexWrap="nowrap">
      <Grid item>
        <DateRangePresetPicker value={range} onChange={handleChange} label="Order date" />
      </Grid>
      {(dateFrom || dateTo) && (
        <Grid item>
          <Button size="small" onClick={handleClear}>
            Clear
          </Button>
        </Grid>
      )}
    </Grid>
  )
}
